import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { HiArrowRight, HiUsers, HiAcademicCap, HiGlobe, HiPhone, HiMail, HiCalendar, HiLocationMarker } from 'react-icons/hi'
import { FaTrophy, FaFistRaised } from 'react-icons/fa'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import { getUpcomingEvents } from '../../data/eventsData'
import { getLocalizedPartnerFederations } from '../../data/partnerFederationsData'
import { getLatestNews } from '../../data/newsData'
import SEO from '../../components/SEO/SEO'
import './Home.css'

function Home() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'

  return (
    <div className="home">
      <SEO 
        description={t('home.aboutDescription')}
        keywords="martial arts, SUMA International, боевые искусства, КУБО, айкидо, айки-дзюцу, дзю-дзюцу"
      />
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">{t('home.title')}</h1>
            <p className="hero-subtitle">{t('home.subtitle')}</p>
            <Link to={getLocalizedLink('/membership')} className="btn btn-primary">
              {t('home.cta')}
              <HiArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <h2 className="section-title">{t('home.aboutTitle')}</h2>
          <p className="section-description">
            {t('home.aboutDescription', 'The International School Union of Martial Arts (SUMA International) unites schools and federations from around the world, promoting the development and preservation of traditional and modern martial arts.')}
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="founder-preview-section">
        <div className="container">
          <div className="founder-preview-content">
            <div className="founder-preview-photo">
              <img 
                src="/images/persons/Grandmaster_Bessonov_Pavel_Gavrilovich.jpg" 
                alt={t('founder.name', 'Pavel Gavrilovich Bessonov')}
              />
            </div>
            <div className="founder-preview-text">
              <h2 className="section-title">{t('home.founderTitle', 'Founder')}</h2>
              <h3 className="founder-preview-name">{t('founder.name', 'Pavel Gavrilovich Bessonov')}</h3>
              <p className="founder-preview-dates">{t('founder.dates', '(05.09.1949 - 05.03.2025)')}</p>
              <p className="founder-preview-description">
                {t('home.founderDescription', 'Pavel Gavrilovich Bessonov was the founder and creator of SUMA International, a distinguished master of martial arts who dedicated his life to the development and promotion of martial arts worldwide. He was also the creator of KUBO (Classical Universal Wrestling) system.')}
              </p>
              <Link to={getLocalizedLink('/founder')} className="btn btn-secondary">
                {t('home.learnMoreAboutFounder', 'Learn More About the Founder')}
                <HiArrowRight className="btn-icon" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="statistics-section">
        <div className="container">
          <h2 className="section-title">{t('home.statistics')}</h2>
          <div className="statistics-grid">
            <div className="stat-card">
              <HiUsers className="stat-icon" />
              <div className="stat-number">9</div>
              <div className="stat-label">{t('home.representatives', 'Representatives')}</div>
            </div>
            <div className="stat-card">
              <HiAcademicCap className="stat-icon" />
              <div className="stat-number">4</div>
              <div className="stat-label">{t('home.federations', 'Federations')}</div>
            </div>
            <div className="stat-card">
              <FaFistRaised className="stat-icon" />
              <div className="stat-number">8</div>
              <div className="stat-label">{t('home.disciplines', 'Martial Arts Disciplines')}</div>
            </div>
            <div className="stat-card">
              <FaTrophy className="stat-icon" />
              <div className="stat-number">2</div>
              <div className="stat-label">{t('home.schools', 'Schools')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Disciplines */}
      <section className="disciplines-section">
        <div className="container">
          <h2 className="section-title">{t('home.popularDisciplines')}</h2>
          <div className="disciplines-grid">
            <Link to={`${getLocalizedLink('/martial-arts')}/kubo`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? 'КУБО' : (isRussian ? 'КУБО' : 'KUBO')}</div>
              <div className="discipline-subtitle">{isJapanese ? '古典的ユニバーサルレスリング' : (isRussian ? 'Классическая Универсальная Борьба' : 'Classical Universal Wrestling')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/aikido`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? '合気道' : (isRussian ? 'Айкидо' : 'Aikido')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/aikijutsu`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? '合気術' : (isRussian ? 'Айки-дзюцу' : 'Aiki-jutsu')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/jujutsu`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? '柔術' : (isRussian ? 'Дзю-дзюцу' : 'Jujutsu')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/aikibujutsu`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? '合気武術' : (isRussian ? 'Айки-бу-дзюцу' : 'Aiki-bu-jutsu')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/karatejutsu`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? '空手術' : (isRussian ? 'Карате-дзюцу' : 'Karate-jutsu')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/kenjutsu`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? '剣術' : (isRussian ? 'Кэн-дзюцу' : 'Kenjutsu')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/kempo`} className="discipline-card">
              <div className="discipline-name">{isJapanese ? '拳法' : (isRussian ? 'Кемпо' : 'Kempo')}</div>
            </Link>
          </div>
        </div>
      </section>

      {/* SUMA Federations */}
      <section className="federations-section">
        <div className="container">
          <h2 className="section-title">{t('home.partnerFederations')}</h2>
          <div className="federations-grid">
            <Link to={`${getLocalizedLink('/federations')}/kubo`} className="federation-card">
              <div className="federation-logo-container">
                <img 
                  src="/images/federations/KUBO Federation.png" 
                  alt={isJapanese ? 'КУБО連盟' : (isRussian ? 'Федерация КУБО' : 'KUBO Federation')}
                  className="federation-logo"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextElementSibling.style.display = 'flex'
                  }}
                />
                <div className="federation-logo-placeholder" style={{ display: 'none' }}>
                  <HiAcademicCap className="placeholder-icon" />
                </div>
              </div>
              <div className="federation-name">{isJapanese ? 'КУБО連盟' : (isRussian ? 'Федерация КУБО' : 'KUBO Federation')}</div>
              <div className="federation-subtitle">{isJapanese ? '古典的ユニバーサルレスリング' : (isRussian ? 'Классическая Универсальная Борьба' : 'Classical Universal Wrestling')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/federations')}/karatejutsu`} className="federation-card">
              <div className="federation-logo-container">
                <img 
                  src="/images/federations/Karate-jutsu Federation.png" 
                  alt={isJapanese ? '空手術連盟' : (isRussian ? 'Федерация Карате-дзюцу' : 'Karate-jutsu Federation')}
                  className="federation-logo"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextElementSibling.style.display = 'flex'
                  }}
                />
                <div className="federation-logo-placeholder" style={{ display: 'none' }}>
                  <HiAcademicCap className="placeholder-icon" />
                </div>
              </div>
              <div className="federation-name">{isJapanese ? '空手術連盟' : (isRussian ? 'Федерация Карате-дзюцу' : 'Karate-jutsu Federation')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/federations')}/jujutsu`} className="federation-card">
              <div className="federation-logo-container">
                <img 
                  src="/images/federations/Jujutsu Federation.png" 
                  alt={isJapanese ? '柔術連盟' : (isRussian ? 'Федерация Дзю-дзюцу' : 'Jujutsu Federation')}
                  className="federation-logo"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextElementSibling.style.display = 'flex'
                  }}
                />
                <div className="federation-logo-placeholder" style={{ display: 'none' }}>
                  <HiAcademicCap className="placeholder-icon" />
                </div>
              </div>
              <div className="federation-name">{isJapanese ? '柔術連盟' : (isRussian ? 'Федерация Дзю-дзюцу' : 'Jujutsu Federation')}</div>
            </Link>
            <Link to={`${getLocalizedLink('/federations')}/aikibujutsu`} className="federation-card">
              <div className="federation-logo-container">
                <img 
                  src="/images/federations/Aiki-bu-jutsu Federation.png" 
                  alt={isJapanese ? '合気武術連盟' : (isRussian ? 'Федерация Айки-бу-дзюцу' : 'Aiki-bu-jutsu Federation')}
                  className="federation-logo"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextElementSibling.style.display = 'flex'
                  }}
                />
                <div className="federation-logo-placeholder" style={{ display: 'none' }}>
                  <HiAcademicCap className="placeholder-icon" />
                </div>
              </div>
              <div className="federation-name">{isJapanese ? '合気武術連盟' : (isRussian ? 'Федерация Айки-бу-дзюцу' : 'Aiki-bu-jutsu Federation')}</div>
            </Link>
          </div>
        </div>
      </section>

      {/* Mutual Recognition */}
      <section className="mutual-recognition-section">
        <div className="container">
          <h2 className="section-title">{t('home.mutualRecognition', 'Mutual Recognition')}</h2>
          <p className="section-description">
            {t('home.mutualRecognitionDescription', 'SUMA International has mutual recognition agreements with the following international federations, promoting cooperation and exchange in martial arts.')}
          </p>
          <div className="partner-federations-grid">
            {getLocalizedPartnerFederations(i18n.language).map(federation => (
              <div key={federation.id} className="partner-federation-card">
                {federation.logo ? (
                  <div className="partner-federation-logo">
                    <img src={federation.logo} alt={federation.name} />
                  </div>
                ) : (
                  <div className="partner-federation-logo-placeholder">
                    <HiAcademicCap className="placeholder-icon" />
                  </div>
                )}
                <h3 className="partner-federation-name">{federation.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Geographic Presence */}
      <section className="geographic-section">
        <div className="container">
          <h2 className="section-title">{t('home.geographicPresence', 'Geographic Presence')}</h2>
          <p className="section-description">
            {t('home.geographicDescription', 'SUMA International has representatives and member schools across multiple countries, creating a global network of martial arts practitioners.')}
          </p>
            <div className="geographic-info">
            <div className="geographic-stats">
              <div className="geo-stat">
                <div className="geo-stat-number">7</div>
                <div className="geo-stat-label">{t('home.countries', 'Countries')}</div>
              </div>
              <div className="geo-stat">
                <div className="geo-stat-number">9</div>
                <div className="geo-stat-label">{t('home.representatives', 'Representatives')}</div>
              </div>
            </div>
            <Link to={getLocalizedLink('/representatives')} className="btn btn-secondary">
              {t('home.viewRepresentatives', 'View Representatives')}
              <HiArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* Outstanding Masters */}
      <section className="masters-section">
        <div className="container">
          <h2 className="section-title">{t('home.outstandingMasters', 'Outstanding Masters')}</h2>
          <p className="section-description">
            {t('home.mastersDescription', 'Recognizing masters who have achieved the highest levels in their martial arts disciplines.')}
          </p>
            <div className="masters-preview">
            <div className="masters-info">
              <div className="masters-stat">
                <div className="masters-stat-number">86</div>
                <div className="masters-stat-label">{t('home.blackBelts', 'Black Belt Holders')}</div>
              </div>
              <div className="masters-stat">
                <div className="masters-stat-number">7</div>
                <div className="masters-stat-label">{t('common.presidium', 'Presidium')}</div>
              </div>
            </div>
            <div className="masters-actions">
              <Link to={getLocalizedLink('/black-belts')} className="btn btn-secondary">
                {t('common.blackBelts')}
              </Link>
              <Link to={getLocalizedLink('/colored-belts')} className="btn btn-secondary">
                {t('common.coloredBelts')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="events-preview-section">
        <div className="container">
          <h2 className="section-title">{t('home.upcomingEvents', 'Upcoming Events')}</h2>
          <p className="section-description">
            {t('home.eventsDescription', 'Discover upcoming competitions, seminars, and workshops organized by SUMA International.')}
          </p>
          {(() => {
            const upcomingEvents = getUpcomingEvents(i18n.language, 3)
            return upcomingEvents.length > 0 ? (
              <div className="events-preview-grid">
                {upcomingEvents.map(event => (
                  <Link key={event.id} to={getLocalizedLink('/events')} className="event-preview-card">
                    {event.poster && (
                      <div className="event-preview-poster">
                        <img src={event.poster} alt={event.title} />
                      </div>
                    )}
                    <div className="event-preview-content">
                      <div className="event-preview-date">
                        <HiCalendar className="event-preview-icon" />
                        <span>{event.dateFormatted || event.date}</span>
                      </div>
                      <h3 className="event-preview-title">{event.title}</h3>
                      <div className="event-preview-location">
                        <HiLocationMarker className="event-preview-icon" />
                        <span>{event.location}</span>
                      </div>
                      <p className="event-preview-description">{event.description.split('\n')[0]}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="no-events-preview">
                <p>{t('home.noUpcomingEvents', 'No upcoming events at the moment. Check back soon!')}</p>
              </div>
            )
          })()}
          <div className="events-preview-action">
            <Link to={getLocalizedLink('/events')} className="btn btn-secondary">
              {t('home.viewAllEvents', 'View All Events')}
              <HiArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section className="news-section">
        <div className="container">
          <h2 className="section-title">{t('home.latestNews', 'Latest News')}</h2>
          <p className="section-description">
            {t('home.newsDescription', 'Stay updated with the latest news and announcements from SUMA International.')}
          </p>
          {(() => {
            const latestNews = getLatestNews(i18n.language, 2)
            return latestNews.length > 0 ? (
              <div className="news-preview-grid">
                {latestNews.map(news => (
                  <Link key={news.id} to={`${getLocalizedLink('/news')}/${news.id}`} className="news-preview-card">
                    {news.image && (
                      <div className="news-preview-image-wrapper">
                        <img 
                          src={news.image} 
                          alt={news.title}
                          className="news-preview-image"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="news-preview-content">
                      <div className="news-preview-date">
                        <HiCalendar className="news-preview-icon" />
                        <span>{news.date}</span>
                      </div>
                      <h3 className="news-preview-title">{news.title}</h3>
                      <p className="news-preview-excerpt">{news.preview}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : null
          })()}
          <div className="news-action">
            <Link to={getLocalizedLink('/news')} className="btn btn-secondary">
              {t('home.viewAllNews', 'View All News')}
              <HiArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">{t('home.quickContact')}</h2>
          <p className="section-description">
            {t('home.contactDescription', 'Get in touch with SUMA International. We are here to answer your questions and help you join our community.')}
          </p>
          <div className="contact-cards">
            <div className="contact-card">
              <div className="contact-card-icon">
                <HiPhone />
              </div>
              <div className="contact-card-content">
                <h3 className="contact-card-title">{t('home.phone', 'Phone')}</h3>
                <a href="tel:+73432269909" className="contact-card-value">+7 (343) 226-99-09</a>
              </div>
            </div>
            <div className="contact-card">
              <div className="contact-card-icon">
                <HiMail />
              </div>
              <div className="contact-card-content">
                <h3 className="contact-card-title">{t('home.email', 'Email')}</h3>
                <a href="mailto:sumaunion@yandex.ru" className="contact-card-value">sumaunion@yandex.ru</a>
              </div>
            </div>
          </div>
          <div className="contact-action">
            <Link to={getLocalizedLink('/contact')} className="btn btn-primary">
              {t('home.contactButton', 'Contact Us')}
              <HiArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home

